import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: true,
  error: null
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'USER_LOADED':
      return {
        ...state,
        isAuthenticated: true,
        loading: false,
        user: action.payload,
        error: null
      };
    case 'REGISTER_SUCCESS':
    case 'LOGIN_SUCCESS':
      localStorage.setItem('token', action.payload.token);
      return {
        ...state,
        ...action.payload,
        isAuthenticated: true,
        loading: false,
        error: null
      };
    case 'AUTH_ERROR':
    case 'LOGIN_FAIL':
    case 'REGISTER_FAIL':
    case 'LOGOUT':
      localStorage.removeItem('token');
      return {
        ...state,
        token: null,
        isAuthenticated: false,
        loading: false,
        user: null,
        error: action.payload
      };
    case 'CLEAR_ERRORS':
      return {
        ...state,
        error: null
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Cargar usuario si hay token
  useEffect(() => {
    if (state.token) {
      loadUser();
    } else {
      dispatch({ type: 'AUTH_ERROR' });
    }
  }, []);

  // Cargar usuario
  const loadUser = async () => {
    try {
      const res = await authAPI.getProfile();
      dispatch({
        type: 'USER_LOADED',
        payload: res.data.data
      });
    } catch (error) {
      dispatch({ type: 'AUTH_ERROR' });
    }
  };

  // Registrar usuario
  const register = async (userData) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const res = await authAPI.register(userData);
      dispatch({
        type: 'REGISTER_SUCCESS',
        payload: res.data.data
      });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Error en el registro';
      dispatch({
        type: 'REGISTER_FAIL',
        payload: message
      });
      return { success: false, message };
    }
  };

  // Login
  const login = async (credentials) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const res = await authAPI.login(credentials);
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: res.data.data
      });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Error en el login';
      dispatch({
        type: 'LOGIN_FAIL',
        payload: message
      });
      return { success: false, message };
    }
  };

  // Logout
  const logout = () => {
    dispatch({ type: 'LOGOUT' });
  };

  // Limpiar errores
  const clearErrors = () => {
    dispatch({ type: 'CLEAR_ERRORS' });
  };

  // Crear admin (solo desarrollo)
  const createAdmin = async (adminData) => {
    try {
      const res = await authAPI.createAdmin(adminData);
      return { success: true, data: res.data.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Error creando administrador';
      return { success: false, message };
    }
  };

  const value = {
    ...state,
    register,
    login,
    logout,
    clearErrors,
    createAdmin,
    loadUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;